import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Loader2,
  Search,
  Sparkles,
  Cpu,
  Newspaper,
  Users,
  Lock,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import axios from "axios";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

function scoreTier(score) {
  if (score >= 70) return { tone: "text-[#2E7D32]", bg: "bg-[#E8F5E9]", label: "High Priority" };
  if (score >= 40) return { tone: "text-[#F57F17]", bg: "bg-[#FFF8E1]", label: "Medium Priority" };
  return { tone: "text-[#C62828]", bg: "bg-[#FFEBEE]", label: "Low Priority" };
}

/**
 * Anonymous top-of-funnel widget. Visitor types a domain → gets a teaser
 * enrichment in ~10s. Live tech stack + opportunity score + locked sections
 * with sign-up CTAs.
 */
export default function TryItWidget() {
  const [domain, setDomain] = useState("");
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");

  const run = async (e) => {
    e?.preventDefault?.();
    setError("");
    setPreview(null);
    const d = domain.trim().toLowerCase();
    if (!d || !/\./.test(d) || /\s/.test(d)) {
      setError("Enter a real domain like apollo.io or hubspot.com");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_BASE}/api/public/try-it`, { domain: d });
      setPreview(data);
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          "We couldn't enrich that domain right now. Try a different one or sign up for the full experience.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="bg-ink-50/40 border-y border-ink-100 py-20" data-testid="try-it-widget">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-1.5 bg-brand/10 text-brand text-[10px] uppercase tracking-widestx font-bold px-2.5 py-1.5 rounded-sm mb-4">
            <Sparkles className="w-2.5 h-2.5" />
            Try before you sign up
          </div>
          <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter">
            See what LeadIQ knows
            <br />
            <span className="text-brand">about any company.</span>
          </h2>
          <p className="mt-4 text-ink-500 max-w-xl mx-auto text-base">
            Drop a domain. We'll enrich it live and show you a real preview — no card,
            no signup, no catch. 3 free previews per hour.
          </p>
        </div>

        <form
          onSubmit={run}
          className="max-w-2xl mx-auto bg-white border border-ink-100 rounded-sm p-2 flex items-center gap-2 shadow-lg shadow-ink-900/5"
        >
          <Search className="w-5 h-5 text-ink-400 ml-2 shrink-0" />
          <Input
            type="text"
            placeholder="apollo.io"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            data-testid="tryit-domain-input"
            disabled={loading}
            className="border-0 bg-transparent text-base font-mono focus-visible:ring-0 focus-visible:ring-offset-0 flex-1"
          />
          <Button
            type="submit"
            disabled={loading || !domain.trim()}
            data-testid="tryit-submit"
            className="rounded-sm bg-brand hover:bg-brand-hover text-white font-semibold h-11 px-6"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Enriching…
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Enrich it
              </>
            )}
          </Button>
        </form>

        {error && (
          <div
            className="max-w-2xl mx-auto mt-4 px-4 py-3 bg-[#FFEBEE] border border-[#FFCDD2] text-[#C62828] text-sm rounded-sm text-center"
            data-testid="tryit-error"
          >
            {error}
          </div>
        )}

        {/* Loading skeleton */}
        {loading && !preview && (
          <div className="max-w-3xl mx-auto mt-8 text-center text-ink-400 text-sm">
            <p className="mb-2">Researching {domain.trim()} live — this takes ~10 seconds…</p>
            <div className="flex justify-center gap-1.5 mt-3">
              {[0, 150, 300].map((d) => (
                <div
                  key={d}
                  className="w-1.5 h-1.5 rounded-full bg-brand animate-pulse"
                  style={{ animationDelay: `${d}ms` }}
                />
              ))}
            </div>
          </div>
        )}

        {/* Result */}
        {preview && <PreviewCard preview={preview} />}
      </div>
    </section>
  );
}

function PreviewCard({ preview }) {
  const tier = scoreTier(preview.opportunity_score);
  const overview = preview.company_overview || {};
  return (
    <div
      className="max-w-3xl mx-auto mt-8 bg-white border border-ink-100 rounded-sm overflow-hidden shadow-xl shadow-ink-900/10 fade-up"
      data-testid="tryit-preview"
    >
      {/* header */}
      <div className="px-6 py-5 border-b border-ink-100 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400">
            Live enrichment preview
          </div>
          <div className="font-display font-black text-2xl text-ink-900 mt-0.5">
            {preview.domain}
          </div>
          {overview.industry && (
            <div className="text-sm text-ink-500 mt-0.5">{overview.industry}</div>
          )}
        </div>
        <div className={`px-4 py-2.5 rounded-sm border ${tier.bg} min-w-[110px] text-center`}>
          <div className={`font-mono text-3xl font-black tracking-tighter leading-none ${tier.tone}`}>
            {preview.opportunity_score}
          </div>
          <div className={`text-[9px] uppercase tracking-widestx font-semibold ${tier.tone} mt-0.5`}>
            opp · /100
          </div>
        </div>
      </div>

      {/* body */}
      <div className="px-6 py-5 grid sm:grid-cols-2 gap-5">
        {overview.description && (
          <div className="sm:col-span-2">
            <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 mb-1.5">
              About
            </div>
            <p className="text-sm text-ink-700 leading-relaxed">{overview.description}</p>
          </div>
        )}

        <div>
          <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 mb-2 flex items-center gap-1.5">
            <Cpu className="w-3 h-3" />
            Tech stack peek
            {preview.tech_categories_count > 0 && (
              <span className="text-ink-400">· {preview.tech_categories_count} categories</span>
            )}
          </div>
          {preview.tech_stack_preview?.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {preview.tech_stack_preview.map((t) => (
                <span
                  key={t}
                  className="text-xs bg-ink-50 border border-ink-100 text-ink-700 px-2.5 py-1 rounded-sm font-mono"
                >
                  {t}
                </span>
              ))}
              <span className="text-xs text-ink-400 italic px-2 py-1">+ more, signed in</span>
            </div>
          ) : (
            <p className="text-xs text-ink-400 italic">No public signals detected</p>
          )}
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 mb-2 flex items-center gap-1.5">
            <Users className="w-3 h-3" />
            Signals
          </div>
          <div className="space-y-1.5 text-sm text-ink-700">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-3.5 h-3.5 text-[#2E7D32]" />
              {preview.hiring_count} hiring signal{preview.hiring_count === 1 ? "" : "s"} detected
            </div>
            <div className="flex items-center gap-2">
              <Newspaper className="w-3.5 h-3.5 text-[#0D47A1]" />
              {preview.news_count} recent news item{preview.news_count === 1 ? "" : "s"}
            </div>
          </div>
        </div>

        {preview.top_news && (
          <div className="sm:col-span-2 border-t border-ink-100 pt-4">
            <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 mb-1.5 flex items-center gap-1.5">
              <Newspaper className="w-3 h-3" />
              Latest news headline
            </div>
            <a
              href={preview.top_news.link || preview.top_news.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-ink-900 hover:text-brand font-medium leading-snug block"
            >
              {preview.top_news.title}
            </a>
          </div>
        )}
      </div>

      {/* Locked CTA */}
      <div className="bg-gradient-to-br from-ink-900 to-ink-700 text-white px-6 py-6">
        <div className="flex items-start gap-3">
          <Lock className="w-5 h-5 text-brand shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-display font-extrabold text-lg leading-tight">
              The good stuff is behind sign-up.
            </div>
            <ul className="mt-3 grid sm:grid-cols-2 gap-x-4 gap-y-1.5 text-[13px] text-white/85">
              {Object.values(preview._locked || {}).map((line, i) => (
                <li key={i} className="flex items-start gap-1.5">
                  <span className="text-brand mt-1 leading-none">•</span>
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <Link
          to="/signup"
          data-testid="tryit-signup-cta"
          className="mt-5 inline-flex items-center gap-2 bg-brand hover:bg-brand-hover text-white font-semibold px-5 py-3 rounded-sm text-sm transition-colors"
        >
          Get the full report — free
          <ArrowRight className="w-4 h-4" />
        </Link>
        <span className="ml-3 text-[11px] text-white/60">
          30-day Apex trial · no card required
        </span>
      </div>
    </div>
  );
}
